import React, { useState, useRef, useEffect, useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBars,
  faChevronDown,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import { Link } from "react-router-dom";
import { Collapse } from "react-bootstrap";
import { useUser } from "context/UserContext";
import ProfileDropdown from "./ProfileDropdown";

const SideBar2 = () => {
  const { isAuthenticated } = useUser();
  const [collapsed, setCollapsed] = useState(true);
  const [hoveredIdx, setHoveredIdx] = useState(null);
  const [openIndex, setOpenIndex] = useState(null);
  const [openSubIndex, setOpenSubIndex] = useState(null);
  const [openBilling, setOpenBilling] = useState(false);
  const [openHistory, setOpenHistory] = useState(false);
  const sidebarRef = useRef(null);
  const handleToggle = (index) => {
    if (menuItems[index].submenu) {
      setCollapsed(false);
    }
    setOpenIndex(openIndex === index ? null : index);
    // Close billing and history sections when opening main menu items
    if (openIndex !== index) {
      setOpenBilling(false);
      setOpenHistory(false);
    }
  };

  const handleSubToggle = (index) => {
    setCollapsed(false);
    setOpenSubIndex(openSubIndex === index ? null : index);
  };

  const handleBillingToggle = () => {
    setCollapsed(false);
    setOpenBilling(!openBilling);
    // Close other sections when opening billing
    if (!openBilling) {
      setOpenIndex(null);
      setOpenSubIndex(null);
      setOpenHistory(false);
    }
  };

  const handleHistoryToggle = () => {
    setCollapsed(false);
    setOpenHistory(!openHistory);
    // Close other sections when opening history
    if (!openHistory) {
      setOpenIndex(null);
      setOpenSubIndex(null);
      setOpenBilling(false);
    }
  };

  const menuItems = useMemo(
    () => [
      { iconUrl: "/icon/overview.svg", label: "Home", link: "/home" },
      {
        iconUrl: "/icon/edit-two.svg",
        label: "Content Editor",
        submenu: [
          {
            name: "Content Journey",
            submenu: [
              {
                name: "Keyword",
                link: "/content_editor/content_journey/keyword",
              },
              {
                name: "URL",
                link: "/content_editor/content_journey/url",
              },
            ],
          },
          {
            name: "Headline Generation",
            submenu: [
              {
                name: "Keyword",
                link: "/content_editor/headlines/keyword",
              },
              {
                name: "URL",
                link: "/content_editor/headlines/url",
              },
            ],
          },
          {
            name: "Writer",
            submenu: [
              {
                name: "URL",
                link: "/content_editor/writer/url",
              },
              {
                name: "Keyword",
                link: "/content_editor/writer/keyword",
              },
              {
                name: "Content",
                link: "/content_editor/writer/content",
              }
            ],
          },
          {
            name: "Keyword Discovery",
            submenu: [
              {
                name: "Keyword",
                link: "/content_editor/keyword_discovery/keyword",
              },
              {
                name: "URL",
                link: "/content_editor/keyword_discovery/url",
              },
            ],
          },
          {
            name: "AI Overview",
            submenu: [
              {
                name: "Keyword",
                link: "/content_editor/ai_overview/keyword",
              },
              {
                name: "URL",
                link: "/content_editor/ai_overview/url",
              },
              {
                name: "Content",
                link: "/content_editor/ai_overview/content",
              },
            ],
          },
          {
            name: "Import Content",
            submenu: [
              {
                name: "Content",
                link: "/content_editor/import_content/content",
              },
            ],
          }
        ],
      },
      {
        iconUrl: "/icon/positive-dynamics.svg",
        label: "SEO",
        submenu: [
          {
            name: "Keyword Research",
            submenu: [
              { name: "Keyword", link: "/seo/keyword_research/keyword" },
              { name: "URL", link: "/seo/keyword_research/url" },
            ],
          },
          {
            name: "SEO Title",
            submenu: [
              { name: "Keyword", link: "/seo/title/keyword" },
              { name: "URL", link: "/seo/title/url", disabled: true },
            ],
          },
          {
            name: "SEO Meta Description",
            submenu: [
              { name: "Keyword", link: "/seo/meta_description/keyword" },
              {
                name: "URL",
                link: "/seo/meta_description/url",
                disabled: true,
              },
            ],
          },
          {
            name: "Ranking",
            submenu: [
              {
                name: "Keyword",
                link: "/seo/ranking/keyword",
              },
              {
                name: "Domain Ranking",
                link: "/seo/ranking/domain_ranking",
                disabled: true,
              },
              {
                name: "Page Ranking",
                link: "/seo/ranking/page_ranking",
                disabled: true,
              },
            ],
          },
          {
            name: "Image",
            submenu: [
              {
                name: " Keyword-Based Optimisation",
                link: "/seo/image/keyword_optimisation",
              },
              {
                name: " ALT Tag Optimisation",
                link: "/seo/image/alt_tag_optimisation",
                disabled: true,
              },
            ],
          },
        ],
      },
      {
        iconUrl: "/icon/more-app.svg",
        label: "Social",
        submenu: [
          {
            name: "Meta + Insta",
            submenu: [
              { name: "Tags", link: "/social/meta_insta/tags", disabled: true },
              {
                name: "Content Creation",
                link: "/social/meta_insta/content_creation",
                disabled: true,
              },
            ],
          },
        ],
      }
    ],
  );

  useEffect(() => {
    function handleClickOutside(event) {
      if (sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        // Only collapse if clicked item doesn't have submenu
        const clickedMenuItem = menuItems.find((item, idx) =>
          event.target.closest(`[data-idx="${idx}"]`)
        );
        // Don't collapse if clicking on profile dropdown
        const isProfileDropdown = event.target.closest(".profile-dropdown");
        if (!clickedMenuItem?.submenu && !isProfileDropdown) {
          setCollapsed(true);
        }
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [menuItems]);

  const billingItems = [
    { name: "Plans", link: "/plans" },
    { name: "Transactions", link: "/plans/transactions" },
    { name: "Actions", link: "/billing/activity" },
  ];

  const historyItems = [
    { name: "Activity", link: "/billing/activity" }
  ];
  const sidebarStyle = {
    position: "fixed",
    top: 50,
    left: 0,
    height: "calc(100vh - 50px)",
    background: "#f8f9fa",
    borderRight: "1px solid #FBFBFB",
    display: "flex",
    flexDirection: "column",
    alignItems: collapsed ? "center" : "flex-start",
    padding: 15,
    zIndex: 10,
    overflowY: "auto",
  };

  const menuIconStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    padding: 15,
  };

  const menuItemStyle = (hovered) => ({
    display: "flex",
    alignItems: "center",
    width: "100%",
    padding: "10px 15px",
    cursor: "pointer",
    borderRadius: 8,
    marginBottom: 8,
    background: hovered ? "#EDEDED" : "transparent",
  });

  const iconStyle = {
    fontSize: 20,
    marginRight: collapsed ? 0 : 16,
    minWidth: 20,
    textAlign: "center",
  };

  return (
    // hide the sidebar on small screens
    <div style={sidebarStyle} ref={sidebarRef} className="d-none d-sm-flex">
      <div onClick={() => setCollapsed((c) => !c)} style={menuIconStyle}>
        <FontAwesomeIcon icon={faBars} size="xl" />
      </div>

      {/* Main Menu Items */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
        {menuItems.map((item, idx) => (
          <div key={idx} data-idx={idx}>
            {item.submenu ? (
              <div>
                <div
                  style={menuItemStyle(hoveredIdx === idx)}
                  onMouseEnter={() => setHoveredIdx(idx)}
                  onMouseLeave={() => setHoveredIdx(null)}
                  onClick={() => handleToggle(idx)}
                >
                  <span style={iconStyle}>
                    <img
                      src={item.iconUrl}
                      alt={item.label}
                      style={{ width: 25, height: 25 }}
                    />
                  </span>
                  {!collapsed && (
                    <>
                      <span style={{ flex: 1 }}>{item.label}</span>
                      <FontAwesomeIcon
                        icon={
                          openIndex === idx ? faChevronDown : faChevronRight
                        }
                        style={{ marginLeft: 8 }}
                      />
                    </>
                  )}
                </div>
                <Collapse in={openIndex === idx && !collapsed} timeout={0}>
                  <div>
                    {item.submenu.map((subItem, subIdx) => (
                      <div key={subIdx}>
                        {subItem.submenu ? (
                          <div>
                            <div
                              style={{
                                ...menuItemStyle(
                                  hoveredIdx === `${idx}-${subIdx}`
                                ),
                                paddingLeft: 65,
                              }}
                              onMouseEnter={() =>
                                setHoveredIdx(`${idx}-${subIdx}`)
                              }
                              onMouseLeave={() => setHoveredIdx(null)}
                              onClick={() =>
                                handleSubToggle(`${idx}-${subIdx}`)
                              }
                            >
                              <span style={{ flex: 1 }}>{subItem.name}</span>
                              <FontAwesomeIcon
                                icon={
                                  openSubIndex === `${idx}-${subIdx}`
                                    ? faChevronDown
                                    : faChevronRight
                                }
                                style={{ marginLeft: 8 }}
                              />
                            </div>
                            <Collapse
                              in={
                                openSubIndex === `${idx}-${subIdx}` &&
                                !collapsed
                              }
                              timeout={0}
                            >
                              <div>
                                {subItem.submenu.map((subSubItem, subSubIdx) =>
                                  subSubItem.disabled ? (
                                    <div
                                      key={subSubIdx}
                                      style={{
                                        ...menuItemStyle(false),
                                        paddingLeft: 75,
                                        opacity: 0.5,
                                        cursor: "not-allowed",
                                        color: "#999",
                                      }}
                                    >
                                      <span>{subSubItem.name}</span>
                                    </div>
                                  ) : (
                                    <Link
                                      key={subSubIdx}
                                      to={subSubItem.link}
                                      style={{
                                        textDecoration: "none",
                                        color: "inherit",
                                      }}
                                    >
                                      <div
                                        style={{
                                          ...menuItemStyle(
                                            hoveredIdx ===
                                              `${idx}-${subIdx}-${subSubIdx}`
                                          ),
                                          paddingLeft: 75,
                                        }}
                                        onMouseEnter={() =>
                                          setHoveredIdx(
                                            `${idx}-${subIdx}-${subSubIdx}`
                                          )
                                        }
                                        onMouseLeave={() => setHoveredIdx(null)}
                                      >
                                        <span>{subSubItem.name}</span>
                                      </div>
                                    </Link>
                                  )
                                )}
                              </div>
                            </Collapse>
                          </div>
                        ) : (
                          <Link
                            to={subItem.link}
                            style={{ textDecoration: "none", color: "inherit" }}
                          >
                            <div
                              style={{
                                ...menuItemStyle(
                                  hoveredIdx === `${idx}-${subIdx}`
                                ),
                                paddingLeft: 45,
                              }}
                              onMouseEnter={() =>
                                setHoveredIdx(`${idx}-${subIdx}`)
                              }
                              onMouseLeave={() => setHoveredIdx(null)}
                            >
                              <span>{subItem.name}</span>
                            </div>
                          </Link>
                        )}
                      </div>
                    ))}
                  </div>
                </Collapse>
              </div>
            ) : (
              <Link
                to={item.link}
                style={{ textDecoration: "none", color: "inherit" }}
              >
                <div
                  style={menuItemStyle(hoveredIdx === idx)}
                  onMouseEnter={() => setHoveredIdx(idx)}
                  onMouseLeave={() => setHoveredIdx(null)}
                >
                  <span style={iconStyle}>
                    <img
                      src={item.iconUrl}
                      alt={item.label}
                      style={{ width: 25, height: 25 }}
                    />
                  </span>
                  {!collapsed && <span>{item.label}</span>}
                </div>
              </Link>
            )}
          </div>
        ))}
      </div>

      {/* Billing Section */}
      <div
        style={{
          paddingTop: "5px",
          width: "100%",
          paddingBottom: "5px",
        }}
      >
        <div
          style={menuItemStyle(hoveredIdx === "billing")}
          onMouseEnter={() => setHoveredIdx("billing")}
          onMouseLeave={() => setHoveredIdx(null)}
          onClick={handleBillingToggle}
        >
          <span style={iconStyle}>
            <img
              src="/icon/wallet.svg"
              alt="Billing"
              style={{ width: 25, height: 25 }}
            />
          </span>
          {!collapsed && (
            <>
              <span style={{ flex: 1 }}>Billing</span>
              <FontAwesomeIcon
                icon={openBilling ? faChevronDown : faChevronRight}
                style={{ marginLeft: 8 }}
              />
            </>
          )}
        </div>
        <Collapse in={openBilling && !collapsed} timeout={0}>
          <div>
            {billingItems.map((item, idx) => (
              <Link
                key={idx}
                to={item.link}
                style={{ textDecoration: "none", color: "inherit" }}
              >
                <div
                  style={{
                    ...menuItemStyle(hoveredIdx === `billing-${idx}`),
                    paddingLeft: 45,
                  }}
                  onMouseEnter={() => setHoveredIdx(`billing-${idx}`)}
                  onMouseLeave={() => setHoveredIdx(null)}
                >
                  <span>{item.name}</span>
                </div>
              </Link>
            ))}
          </div>
        </Collapse>
      </div>

      {/* History Section */}
      <div
        style={{
          paddingTop: "5px",
          width: "100%",
          paddingBottom: "5px",
        }}
      >
        <div
          style={menuItemStyle(hoveredIdx === "history")}
          onMouseEnter={() => setHoveredIdx("history")}
          onMouseLeave={() => setHoveredIdx(null)}
          onClick={handleHistoryToggle}
        >
          <span style={iconStyle}>
            <img
              src="/icon/notification.svg"
              alt="History"
              style={{ width: 25, height: 25 }}
            />
          </span>
          {!collapsed && (
            <>
              <span style={{ flex: 1 }}>History</span>
              <FontAwesomeIcon
                icon={openHistory ? faChevronDown : faChevronRight}
                style={{ marginLeft: 8 }}
              />
            </>
          )}
        </div>
        <Collapse in={openHistory && !collapsed} timeout={0}>
          <div>
            {historyItems.map((item, idx) => (
              <Link
                key={idx}
                to={item.link}
                style={{ textDecoration: "none", color: "inherit" }}
              >
                <div
                  style={{
                    ...menuItemStyle(hoveredIdx === `history-${idx}`),
                    paddingLeft: 45,
                  }}
                  onMouseEnter={() => setHoveredIdx(`history-${idx}`)}
                  onMouseLeave={() => setHoveredIdx(null)}
                >
                  <span>{item.name}</span>
                </div>
              </Link>
            ))}
          </div>
        </Collapse>
      </div>

      {/* User Profile Section at Bottom */}
      {isAuthenticated && (
        <div
          style={{
            paddingTop: "5px",
            marginTop: "5px",
            width: "100%",
            paddingBottom: "5px",
          }}
        >
          <ProfileDropdown collapsed={collapsed} />
        </div>
      )}
    </div>
  );
};

export default SideBar2;
